import type { Lot } from '../../types/lot'

export type ProcessingSiteSummary = {
  totalLots: number
  activeLots: number
  totalBiomassKg: number | null
  speciesLabels: string[]
  latestActivityLotKey: string | null
  latestActivityAt: string | null
  recentLots: Array<{
    lotId: string
    lotKey: string
    stage: Lot['stage']
    activityAt: string
    sourceLotKey: string | null
  }>
}

const compareText = (left: string, right: string): number => left.localeCompare(right)

const normalizeOptionalText = (value?: string): string | null => {
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  return normalized.length > 0 ? normalized : null
}

const readLotActivityTimestamp = (lot: Lot): number | null => {
  const rawValue = lot.updatedAt ?? lot.createdAt
  if (typeof rawValue !== 'string' || rawValue.trim().length === 0) {
    return null
  }

  const timestamp = Date.parse(rawValue)
  return Number.isNaN(timestamp) ? null : timestamp
}

const readRecentLotSourceKey = (lot: Pick<Lot, 'lotKey' | 'sourceLotKey'>): string | null => {
  const sourceLotKey = normalizeOptionalText(lot.sourceLotKey)
  const lotKey = normalizeOptionalText(lot.lotKey)

  if (!sourceLotKey || sourceLotKey === lotKey) {
    return null
  }

  return sourceLotKey
}

export const buildProcessingSiteSummary = (lots: Lot[]): ProcessingSiteSummary => {
  const speciesLabels = Array.from(new Set(
    lots
      .map((lot) => String(lot.species ?? '').trim())
      .filter((value) => value.length > 0)
  )).sort(compareText)

  const biomassValues = lots
    .map((lot) => lot.biomassKg)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))

  const totalBiomassKg = biomassValues.length > 0
    ? biomassValues.reduce((sum, value) => sum + value, 0)
    : null

  const latestActivityLot = lots.reduce<Lot | null>((currentLatest, lot) => {
    const nextTimestamp = readLotActivityTimestamp(lot)
    if (nextTimestamp === null) {
      return currentLatest
    }

    if (!currentLatest) {
      return lot
    }

    const currentTimestamp = readLotActivityTimestamp(currentLatest)
    if (currentTimestamp === null || nextTimestamp > currentTimestamp) {
      return lot
    }

    return currentLatest
  }, null)

  const recentLots = lots
    .map((lot) => ({
      lotId: lot.id,
      lotKey: lot.lotKey ?? lot.id,
      stage: lot.stage,
      activityAt: lot.updatedAt ?? lot.createdAt ?? '',
      sourceLotKey: lot.sourceLotKey,
      activityTimestamp: readLotActivityTimestamp(lot)
    }))
    .filter((lot) => lot.activityTimestamp !== null && lot.activityAt.length > 0)
    .sort((left, right) => (right.activityTimestamp ?? 0) - (left.activityTimestamp ?? 0))
    .slice(0, 3)
    .map(({ lotId, lotKey, stage, activityAt, sourceLotKey }) => ({
      lotId,
      lotKey,
      stage,
      activityAt,
      sourceLotKey: readRecentLotSourceKey({ lotKey, sourceLotKey })
    }))

  return {
    totalLots: lots.length,
    activeLots: lots.filter((lot) => lot.stage === 'Active').length,
    totalBiomassKg,
    speciesLabels,
    latestActivityLotKey: latestActivityLot?.lotKey ?? latestActivityLot?.id ?? null,
    latestActivityAt: latestActivityLot?.updatedAt ?? latestActivityLot?.createdAt ?? null,
    recentLots
  }
}